<div class="mb-4">
  <!-- Toolbar actions (Import / Show PDF / Delete) are registered declaratively
       in the component via PageHeaderService.setPageActions(...). -->

  <!-- PDF Preview (when toggled on) -->
  @if (receipt?.has_file && showPdfPreview) {
    <div class="w-full mt-3">

      <!-- Download button above the preview -->
      <div class="flex justify-end mb-2">
        <a
          class="btn btn-success btn-sm"
          [href]="receipt.file_url"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download PDF"
        >
          <fa-icon [icon]="faDownload"></fa-icon>
          <span class="ms-1">Download PDF</span>
        </a>
      </div>

      <!-- Full viewport-ish preview -->
      <div class="w-full border border-base-200 rounded-lg overflow-hidden"
           style="height: calc(100vh - 290px);">
        <iframe
          [src]="getPdfUrl()"
          class="w-full h-full"
          title="Receipt PDF Preview">
        </iframe>
      </div>
    </div>
  }

  <!-- Progress and Product List (when PDF preview is off) -->
  @if (!showPdfPreview) {
    <div class="w-full mt-3">
      <div class="card bg-base-100 transition-colors duration-250"
           [class.bg-success]="importFinishedFlash"
           [class.bg-opacity-10]="importFinishedFlash">
        <div class="card-body p-4">
          <div class="font-semibold mb-2">Import progress</div>
          <ui-progress
            class="w-full"
            [color]="'primary'"
            [value]="getTotalSelected()"
            [max]="getTotalUnimported()">
          </ui-progress>
          <small class="text-base-content/60 block mt-1">
            {{ getTotalSelected() }} / {{ getTotalUnimported() }} selected
          </small>
        </div>
      </div>
    </div>
  }

  <!-- Unified responsive layout with DaisyUI collapse -->
  <!-- Receipt Items: Mobile = Collapse, Desktop = Master/Detail -->
  @if (!showPdfPreview) {

    <!-- MOBILE: collapses -->
    <div class="md:hidden mt-4">
      @for (item of sortedReceiptItems; track item) {
        <div
          class="collapse collapse-arrow bg-base-100 border border-base-200 rounded-box mb-3 transition-all duration-500"
          [attr.data-receipt-item-id]="item.id"
          [class.opacity-60]="item.is_imported"
          [ngClass]="{
            'ring-2 ring-primary bg-primary/5 shadow-sm': highlightedReceiptItemId === item.id
          }">

          <!-- DaisyUI collapse uses a checkbox to control open state -->
          <input
            type="checkbox"
            [checked]="isExpanded(item)"
            (change)="setExpanded(item, $any($event.target).checked)"
            (click)="$event.stopPropagation()"
          />

          <div class="collapse-title px-3 py-3" (click)="selectItem(item)">
            <div class="flex items-center justify-between gap-3">
              <div class="flex gap-2 min-w-0 flex-1">
                <fa-icon [icon]="getTypeIcon(item.type)"
                         [style.color]="getTypeColor(item.type)"
                         size="lg"></fa-icon>

                <div class="min-w-0">
                  <div class="font-semibold truncate">{{ item.name }}</div>
                  <div class="text-xs text-base-content/60">
                    Qty: {{ item.quantity }}
                    @if (item.price_per_unit !== null) {
                      <span class="ms-3">{{ item.price_total | price }} {{ item.currency }}</span>
                    }
                  </div>
                </div>
              </div>

              <!-- Mapping state badge: vertically centered + padded away from the collapse arrow -->
              <div class="shrink-0 flex items-center pr-6">
                @if (item.is_imported) {
                  <span class="badge badge-success badge-sm">Imported</span>
                } @else if ((item.selected_product_candidate_id ?? null) !== null) {
                  <span class="badge badge-primary badge-sm">Mapped</span>
                } @else {
                  <!-- <span class="badge badge-ghost badge-sm">Unmapped</span> -->
                }
              </div>
            </div>
          </div>

          <div class="collapse-content px-3 pb-3">
            @if (!item.is_imported) {
              <div class="space-y-4">
                <div>
                  <div class="text-sm font-semibold mb-2">Match product</div>
                  <app-product-candidate-selector
                    [receiptId]="item.receipt_id"
                    [receiptItemId]="item.id"
                    [receiptItemPricePerUnit]="item.price_per_unit"
                    [candidates]="item.product_candidates"
                    [selectedCandidateId]="item.selected_product_candidate_id ?? null"
                    [rawProductName]="item.name"
                    [allowSearch]="(item.selected_product_candidate_id ?? null) === null"
                    [isSearchOpen]="currentSearchContext?.receiptItemId === item.id"
                    (selectionChanged)="productCandidateSelectionChanged(item)"
                    (searchRequested)="onSearchRequested($event)"/>
                </div>

                <!-- Inline catalog search (scoped to this item) -->
                @if (currentSearchContext?.receiptItemId === item.id) {
                  <!-- Mobile uses the catalog search modal. -->
                }

                @if ((item.selected_product_candidate_id ?? null) !== null) {
                  <div>
                    <div class="text-sm font-semibold mb-2">Store</div>
                    <app-store-selector
                      [stores]="stores"
                      [preSelect]="item.target_store_id"
                      [direction]="'horizontal'"
                      (storeSelected)="onStoreSelectionChanged(item, $event)">
                    </app-store-selector>
                  </div>
                }
              </div>
            } @else {
              <div class="space-y-2">
                <div class="text-sm text-base-content/70">
                  <div class="font-semibold">Imported</div>
                  @if (item.imported_product_id !== null) {
                  } @else {
                    <div class="text-xs">(Product info not available)</div>
                  }
                </div>

                <!-- Mobile: imported product preview card -->
                @if (item.id !== null && importedProductByReceiptItemId[item.id] !== null) {
                  @if (importedProductByReceiptItemId[item.id] | async; as importedProduct) {
                    <ui-card
                      [padding]="'sm'"
                      [shadow]="false"
                      [bordered]="true"
                      [className]="'w-full'">

                      <div class="flex items-start gap-3">
                        <div
                          class="w-14 h-14 rounded bg-base-200 overflow-hidden flex items-center justify-center shrink-0">
                          @if (importedProduct?.primary_thumb_image?.url) {
                            <img
                              [src]="importedProduct.primary_thumb_image.url"
                              class="w-full h-full object-cover"
                              alt=""/>
                          } @else {
                            <span class="text-xs text-base-content/60">No img</span>
                          }
                        </div>

                        <div class="min-w-0 flex-1">
                          <div class="text-xs text-base-content/60">Imported product</div>
                          <div class="font-semibold truncate">
                            {{ importedProduct?.name ?? 'Unknown' }}
                          </div>
                          @if (importedProduct?.brands?.length) {
                            <div class="text-xs text-base-content/60 truncate">
                              {{ importedProduct.brands.join(', ') }}
                            </div>
                          }

                          @if (importedProduct?.id) {
                            <a class="link link-primary text-xs" [routerLink]="['/products', importedProduct.id]">
                              Open product details
                            </a>
                          }
                        </div>
                      </div>
                    </ui-card>
                  }
                }
              </div>
            }
          </div>
        </div>
      }
    </div>

    <!-- DESKTOP: master/detail -->
    <div class="hidden md:grid md:grid-cols-2 md:gap-50 mt-4 relative"
         #connectorHost
         [class.has-selection]="selectedItem !== null">

      <!-- Curved connector overlay -->
      <svg class="pointer-events-none absolute inset-0 w-full h-full"
           [class.opacity-0]="connectorPath === null"
           [class.opacity-100]="connectorPath !== null">

        <defs>
          <filter id="receiptBeamGlow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="3" result="blur"/>
            <feMerge>
              <feMergeNode in="blur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        @if (connectorPath !== null) {
          <!-- Filled ribbon (soft solid) -->
          <path
            [attr.d]="connectorPath"
            [attr.fill]="beamColor"
            opacity="0.07"
            filter="url(#receiptBeamGlow)"></path>


        }
      </svg>

      <!-- Master list (flat + compact) -->
      <div class="min-w-0 relative z-10">

        <ul
          #masterList
          class="bg-transparent rounded-box border border-base-200 divide-y-0"
          (scroll)="onMasterListScroll()"
          style="max-height: calc(100vh - 400px); overflow-y: auto;">

          @for (item of sortedReceiptItems; track item) {
            <li
              class="px-2 py-2 cursor-pointer select-none relative"
              [attr.data-receipt-item-id]="item.id"
              [class.opacity-60]="item.is_imported"
              [ngClass]="{ 'bg-primary/10': selectedItem?.id === item.id }"
              (click)="selectItem(item, rowAnchor)">

              <div class="flex items-start justify-between gap-3 w-full pr-6">
                <div class="flex gap-2 min-w-0 flex-1">
                  <fa-icon [icon]="getTypeIcon(item.type)"
                           [style.color]="getTypeColor(item.type)"
                           size="lg"></fa-icon>

                  <div class="min-w-0">
                    <div class="font-semibold truncate">{{ item.name }}</div>
                    <div class="text-xs text-base-content/60">
                      Qty: {{ item.quantity }}
                      @if (item.price_per_unit !== null) {
                        <span class="ms-3">{{ item.price_total | price }} {{ item.currency }}</span>
                      }
                    </div>
                  </div>
                </div>

                <div class="flex items-center gap-2 shrink-0 pt-0.5">
                  @if (item.is_imported) {
                    <span class="badge badge-success badge-sm">Imported</span>
                  } @else if ((item.selected_product_candidate_id ?? null) !== null) {
                    <span class="badge badge-primary badge-sm">Mapped</span>
                  } @else {
                    <!--<span class="badge badge-ghost badge-sm">Unmapped</span>-->
                  }
                </div>
              </div>

              <!-- Beam anchor dot: fixed at the far right edge of the row -->
              <span
                #rowAnchor
                data-beam-anchor="true"
                class="absolute right-2 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full"
                [ngClass]="{
                  'bg-primary ring-2 ring-base-100': selectedItem?.id === item.id,
                  'bg-transparent': selectedItem?.id !== item.id
                }"></span>

            </li>
          }
        </ul>
      </div>

      <!-- Detail panel (centered card width, but no shadow) -->
      <div class="min-w-0 relative z-10">
        <div class="sticky top-4 flex justify-center">
          <div class="w-full max-w-xl border border-base-200 rounded-lg bg-base-100 relative"
               [ngClass]="{ 'ring-1 ring-primary/30': selectedItem !== null }">

            <!-- Beam anchors on the left edge of the detail box -->
            <span
              #detailAnchorTop
              class="hidden md:block absolute -left-3 top-0 w-3 h-3 rounded-full bg-transparent"></span>

            <span
              #detailAnchorBottom
              class="hidden md:block absolute -left-3 bottom-0 w-3 h-3 rounded-full bg-transparent"
            ></span>

            <div class="p-4">

              @if (selectedItem !== null) {

                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div class="text-sm text-base-content/60">Selected item</div>
                    <div class="text-lg font-semibold leading-snug">
                      {{ selectedItem.name }}
                    </div>
                    <div class="text-xs text-base-content/60 mt-1">
                      Qty: {{ selectedItem.quantity }}
                      @if (selectedItem.price_per_unit !== null) {
                        <span class="ms-3">{{ selectedItem.price_total | price }} {{ selectedItem.currency }}</span>
                      }
                    </div>
                  </div>

                  <div class="flex items-start gap-3">
                    <div class="flex items-center gap-2">
                      @if (selectedItem.is_imported) {
                        <span class="badge badge-success">Imported</span>
                      } @else if ((selectedItem.selected_product_candidate_id ?? null) !== null) {
                        <span class="badge badge-primary">Mapped</span>
                      } @else {
                        <span class="badge badge-ghost">Unmapped</span>
                      }
                    </div>
                  </div>
                </div>

                <div class="divider my-3"></div>

                @if (!selectedItem.is_imported) {
                  <div class="space-y-4">
                    <!-- Match candidates: hidden while the catalog search is open to give it more room -->
                    @if (currentSearchContext === null) {
                      <div>
                        <div class="text-sm font-semibold mb-2">Match product</div>
                        <app-product-candidate-selector
                          [receiptId]="selectedItem.receipt_id"
                          [receiptItemId]="selectedItem.id"
                          [receiptItemPricePerUnit]="selectedItem.price_per_unit"
                          [candidates]="selectedItem.product_candidates"
                          [selectedCandidateId]="selectedItem.selected_product_candidate_id ?? null"
                          [rawProductName]="selectedItem.name"
                          [allowSearch]="(selectedItem.selected_product_candidate_id ?? null) === null"
                          [isSearchOpen]="currentSearchContext !== null"
                          (selectionChanged)="productCandidateSelectionChanged(selectedItem)"
                          (searchRequested)="onSearchRequested($event)"/>

                      </div>
                    }

                    <!-- Inline catalog search (no modal) -->
                    @if (currentSearchContext !== null) {
                      <div class="border border-base-200 rounded-lg p-3 bg-base-100">
                        <div class="flex items-center justify-between gap-3 mb-2">
                          <div class="text-sm font-semibold">Catalog search</div>
                          <button type="button" class="btn btn-ghost btn-xs" (click)="currentSearchContext = null">
                            Close
                          </button>
                        </div>

                        @if (catalogSearchKey >= 0) {
                          <app-catalog-search
                            [searchTerm]="catalogSearchTerm"
                            [householdId]="household?.id"
                            [preselectProvider]="receipt.provider || receipt.retailer_name"
                            resultsMaxHeight="30vh"
                            (itemSelected)="onInlineCatalogItemSelected($event)">
                          </app-catalog-search>
                        }
                      </div>
                    }

                    <div>
                      <div class="text-sm font-semibold mb-2">Store</div>
                      <app-store-selector
                        [stores]="stores"
                        [preSelect]="selectedItem.target_store_id"
                        [direction]="'horizontal'"
                        (storeSelected)="onStoreSelectionChanged(selectedItem, $event)">
                      </app-store-selector>
                    </div>

                    <div class="text-xs text-base-content/60">
                      Tipp: Du kannst links schnell durch die Positionen klicken und rechts matchen.
                    </div>
                  </div>
                } @else {
                  <div class="space-y-3">
                    <div class="text-sm text-base-content/70">
                      <div class="font-semibold">Imported</div>
                      @if (selectedItem.imported_product_id !== null) {
                      } @else {
                        <div class="text-xs">(Product info not available)</div>
                      }
                    </div>

                    <!-- Desktop only: imported product preview card (below divider) -->
                    <div class="hidden md:block">
                      @if (importedProduct$ | async; as importedProduct) {
                        <ui-card
                          [padding]="'sm'"
                          [shadow]="false"
                          [bordered]="true"
                          [className]="'w-full'">

                          <div class="flex items-start gap-3">
                            <div
                              class="w-14 h-14 rounded bg-base-200 overflow-hidden flex items-center justify-center shrink-0">
                              @if (importedProduct?.primary_thumb_image?.url) {
                                <img
                                  [src]="importedProduct.primary_thumb_image.url"
                                  class="w-full h-full object-cover"
                                  alt=""/>
                              } @else {
                                <span class="text-xs text-base-content/60">No img</span>
                              }
                            </div>

                            <div class="min-w-0 flex-1">
                              <div class="text-xs text-base-content/60">Imported product</div>
                              <div class="font-semibold truncate">
                                {{ importedProduct?.name ?? 'Unknown' }}
                              </div>
                              @if (importedProduct?.brands?.length) {
                                <div class="text-xs text-base-content/60 truncate">
                                  {{ importedProduct.brands.join(', ') }}
                                </div>
                              }

                              @if (importedProduct?.id) {
                                <a class="link link-primary text-xs" [routerLink]="['/products', importedProduct.id]">
                                  Open product details
                                </a>
                              }
                            </div>
                          </div>
                        </ui-card>
                      }
                    </div>
                  </div>
                }

              } @else {
                <div class="text-base-content/60">
                  Select an item on the left to start mapping.
                </div>
              }

            </div>
          </div>
        </div>
      </div>


    </div>
  }

  <!-- Catalog search modal (mobile) -->
  <app-catalog-modal
    #catalogModal
    [initialSearchTerm]="catalogSearchTerm"
    [householdId]="household?.id"
    [preselectProvider]="receipt.provider || receipt.retailer_name"
    (itemSelected)="onMobileCatalogItemSelected($event)"
    (modalClosed)="onMobileCatalogModalClosed()">
  </app-catalog-modal>
</div>
